<template>
    <PageWrapper>
        <template #headline>
            <fa icon="fa-sack-dollar" />&nbsp;{{ asset.asset_title }}
        </template>

        <template #actions>
            <BackButton
                v-if="$router.options.history.state.back"
                @click="router.go(-1)"
            />
            <BackButton
                v-if="
                    asset.asset_uid &&
                    $router.options.history.state.back !==
                        `/assets/${asset.asset_uid}`
                "
                icon="fa-eye"
                title="asset"
                @click="goToDetails"
            />
            <BackButton
                v-if="$router.options.history.state.back !== '/assets'"
                icon="fa-list"
                title="assets"
                @click="toAssets"
            />
            <SaveButton @click="saveAsset" />
        </template>

        <!-- asset details -->
        <AccordionBasic
            title="asset_details"
            class="mb-2"
            :open="true"
        >
            <TextField
                v-model="asset.asset_title"
                name="asset_title"
                label="asset_title"
                help="help_asset_title"
                :required="true"
            />

            <SelectField
                v-model="asset.platform_name"
                v-model:options="platformOptions"
                name="platform_name"
                label="platform_name"
                help="help_platform_name"
                :required="true"
                :on-search="loadPlatformNameOptions"
            />

            <SelectField
                v-model="asset.issuer_id"
                v-model:options="issuerOptions"
                name="issuer_id"
                label="issuer"
                help="help_issuer"
                :required="true"
                :on-search="loadIssuerOptions"
            />

            <SelectField
                v-model="asset.category"
                name="asset_category"
                label="asset_category"
                help="help_asset_category"
                :options="mapEnumToArray(AssetCategory)"
                :reduce="reduceEnumValue"
                :required="true"
            />
            <SelectField
                v-model="asset.asset_class"
                name="asset_class"
                label="asset_class"
                help="help_asset_class"
                :options="mapEnumToArray(AssetClass)"
                :reduce="reduceEnumValue"
                :required="false"
            />

            <SelectField
                v-model="asset.status"
                name="status"
                label="status"
                help="help_asset_status"
                :options="mapEnumToArray(AssetStatus)"
                :reduce="reduceEnumValue"
                :required="true"
                :clearable="false"
                :class="'asset-status'"
                @update:model-value="retrieveAssetSubStatus"
            />

            <SelectField
                v-model="asset.sub_status"
                :disabled="
                    asset.status === AssetStatus.ACTIVE ||
                    asset.status === AssetStatus.INACTIVE
                "
                name="sub_status"
                label="sub_status"
                help="help_asset_sub_status"
                :options="mapEnumToArray(assetSubStatuses)"
                :reduce="reduceEnumValue"
                :required="false"
            />
        </AccordionBasic>

        <AccordionBasic
            title="asset_settings"
            help="help_asset_settings"
            class="mb-2"
        >
            <ToggleSwitch
                v-model="asset.installment_investment"
                name="asset_installment_investment"
                label="asset_installment_investment"
                help="help_asset_installment_investment"
            />
            <ToggleSwitch
                v-model="asset.hide_payment_info"
                name="asset_hide_payment_info"
                label="asset_hide_payment_info"
                help="help_asset_hide_payment_info"
            />

            <!-- Automatic payments -->
            <ToggleSwitch
                v-model="asset.deactivate_payment_account"
                name="asset_deactivate_payment_account"
                label="asset_deactivate_payment_account"
                help="help_asset_deactivate_payment_account"
            />

            <ToggleSwitch
                v-model="asset.signature_feature_active"
                name="signature_feature_active"
                label="signature_feature_active"
                help="help_signature_feature_active"
            />

            <ToggleSwitch
                v-model="asset.revoke_accrual"
                name="asset_revoke_accrual"
                label="asset_revoke_accrual"
                help="help_asset_revoke_accrual"
            />
        </AccordionBasic>

        <!-- asset location -->
        <AccordionBasic
            title="asset_location"
            class="mb-2"
        >
            <TextField
                v-model="asset.asset_location_city"
                name="asset_location_city"
                label="city"
                help="help_asset_location_city"
            />
            <TextField
                v-model="asset.asset_location_state"
                name="asset_location_state"
                label="state"
                help="help_asset_location_state"
            />

            <SelectField
                v-model="asset.asset_location_country"
                name="asset_location_country"
                label="country"
                help="help_asset_location_country"
                :options="countries"
                :required="true"
            />
        </AccordionBasic>

        <!-- asset funding limits -->
        <AccordionBasic
            title="asset_funding_limits"
            class="mb-2"
        >
            <template v-if="asset.category !== AssetCategory.STOCK_EMISSION">
                <NumberField
                    v-model="asset.funding_min"
                    name="asset_funding_min"
                    label="asset_funding_min"
                    help="help_asset_funding_min"
                    :required="true"
                />
                <NumberField
                    v-model="asset.funding_max"
                    name="asset_funding_max"
                    label="asset_funding_max"
                    help="help_asset_funding_max"
                    :required="true"
                />
                <NumberField
                    v-model="asset.investment_min"
                    name="asset_investment_min"
                    label="asset_investment_min"
                    help="help_asset_investment_min"
                    :required="true"
                />
                <NumberField
                    v-model="asset.investment_max"
                    name="asset_investment_max"
                    label="asset_investment_max"
                    help="help_asset_investment_max"
                    :required="true"
                />
                <NumberField
                    v-model="asset.step_level"
                    name="asset_step_level"
                    label="asset_step_level"
                    help="help_asset_step_level"
                    :required="true"
                />
                <SelectField
                    v-model="asset.currency"
                    name="asset_currency"
                    label="currency"
                    help="help_asset_currency"
                    :options="currencyOptions"
                    :required="true"
                />
                <div
                    class="flex h-auto w-full flex-col items-start justify-start space-y-2"
                >
                    <p class="text-base font-bold">
                        {{ $t('asset_quick_amounts') }}
                    </p>
                    <textarea
                        v-model="stringQuickAmounts"
                        class="h-48 w-1/2 rounded-md border border-gray-300 p-2"
                    ></textarea>
                    <p class="text-sm text-gray-500">
                        {{ $t('help_asset_quick_amounts') }}
                    </p>
                </div>
            </template>
            <!-- stock emission / stock-like asset classes -->
            <NumberField
                v-if="showNominalValue"
                v-model="asset.nominal_value"
                name="asset_nominal_value"
                label="asset_nominal_value"
                :required="true"
            />
            <NumberField
                v-if="showAgio"
                v-model="asset.agio"
                name="asset_agio"
                label="asset_agio"
                help="help_asset_agio"
                :required="true"
            />
            <NumberField
                v-if="showPricePerStockFieldGroup"
                v-model="asset.price_per_stock"
                name="asset_price_per_stock"
                label="asset_price_per_stock"
                help="help_asset_price_per_stock"
                :required="true"
                :readonly="true"
            />
            <NumberField
                v-if="isStockEmissionLegacy"
                v-model="minimum_investment_shares"
                name="asset_minimum_shares"
                label="asset_minimum_shares"
                help="help_asset_minimum_shares"
                :required="true"
            />
            <NumberField
                v-if="showVotingRightsFieldGroup"
                v-model="asset.minimum_investment"
                name="asset_minimum_investment"
                label="asset_minimum_investment"
                help="help_asset_minimum_investment"
                :required="true"
            />
            <NumberField
                v-if="showVotingRightsFieldGroup"
                v-model="asset.number_of_stocks"
                name="asset_number_of_stocks"
                label="asset_number_of_stocks"
                help="help_asset_number_of_stocks"
                :required="true"
            />
            <NumberField
                v-if="showVotingRightsFieldGroup"
                v-model="asset.stock_increments"
                name="asset_stock_increments"
                label="asset_stock_increments"
                help="help_asset_stock_increments"
                :required="true"
            />
            <NumberField
                v-if="showVotingRightsFieldGroup"
                v-model="asset.maximum_total_investment_value"
                name="asset_maximum_total_investment_value"
                label="asset_maximum_total_investment_value"
                help="help_maximum_total_investment_value"
                :required="true"
            />
            <SelectField
                v-if="isStockEmissionLegacy"
                v-model="asset.currency"
                name="asset_currency"
                label="currency"
                help="help_asset_currency"
                :options="currencyOptions"
                :required="true"
            />
        </AccordionBasic>
        <!-- stock details -->
        <AccordionBasic
            v-if="showPricePerStockFieldGroup || showVotingRightsFieldGroup"
            title="stock_details"
        >
            <SelectField
                v-if="showPricePerStockFieldGroup"
                v-model="asset.stock_type"
                name="asset_stock_type"
                label="asset_stock_type"
                help="help_asset_stock_type"
                :options="mapEnumToArray(StockType)"
                :required="true"
            />
            <TextField
                v-model="asset.isin"
                name="asset_isin"
                label="asset_isin"
                help="help_asset_isin"
                :required="true"
            />
            <NumberField
                v-if="showVotingRightsFieldGroup"
                v-model="asset.voting_rights_per_share"
                name="voting_rights_per_share"
                label="voting_rights_per_share"
                help="help_asset_voting_rights_per_share"
                :required="true"
            />
        </AccordionBasic>
        <!-- installment investment -->
        <AccordionBasic
            v-if="asset.installment_investment"
            title="asset_installment_investment"
            class="mb-2"
        >
            <template v-if="asset.installment_investment">
                <h3 class="mt-4 text-base font-bold text-gray-800">
                    {{ $t('asset_initial_payment') }}
                </h3>
                <div class="lg:flex lg:gap-4">
                    <NumberField
                        v-model="asset.minimum_initial_payment"
                        name="asset_minimum_initial_payment"
                        label="asset_minimum_initial_payment"
                        help="help_asset_minimum_initial_payment"
                        class="w-full lg:w-1/3"
                    />
                    <NumberField
                        v-model="asset.maximum_initial_payment"
                        name="asset_maximum_initial_payment"
                        label="asset_maximum_initial_payment"
                        help="help_asset_maximum_initial_payment"
                        class="w-full lg:w-1/3"
                    />
                    <NumberField
                        v-model="asset.initial_payment_step"
                        name="asset_initial_payment_step"
                        label="asset_initial_payment_step"
                        help="help_asset_initial_payment_step"
                        class="w-full lg:w-1/3"
                    />
                </div>
                <h3 class="mt-4 text-base font-bold text-gray-800">
                    {{ $t('asset_installments') }}
                </h3>
                <div class="lg:flex lg:gap-4">
                    <NumberField
                        v-model="asset.minimum_installment"
                        name="asset_minimum_installment"
                        label="asset_minimum_installment"
                        help="asset_minimum_installment"
                        class="w-full lg:w-1/3"
                    />
                    <NumberField
                        v-model="asset.maximum_installment"
                        name="asset_maximum_installment"
                        label="asset_maximum_installment"
                        help="help_asset_maximum_installment"
                        class="w-full lg:w-1/3"
                    />
                    <NumberField
                        v-model="asset.installment_step"
                        name="asset_installment_step"
                        label="asset_installment_step"
                        help="help_asset_installment_step"
                        class="w-full lg:w-1/3"
                    />
                </div>
                <h3 class="mt-4 text-base font-bold text-gray-800">
                    {{ $t('asset_monthly_payments') }}
                </h3>
                <div class="lg:flex lg:gap-4">
                    <NumberField
                        v-model="asset.minimum_monthly_rates"
                        name="asset_minimum_monthly_rates"
                        label="asset_minimum_monthly_rates"
                        help="help_asset_minimum_monthly_rates"
                        class="w-full lg:w-1/4"
                    />
                    <NumberField
                        v-model="asset.maximum_monthly_rates"
                        name="asset_maximum_monthly_rates"
                        label="asset_maximum_monthly_rates"
                        help="help_asset_maximum_monthly_rates"
                        class="w-full lg:w-1/4"
                    />
                    <NumberField
                        v-model="asset.minimum_yearly_rates"
                        name="asset_minimum_yearly_rates"
                        label="asset_minimum_yearly_rates"
                        help="help_asset_minimum_yearly_rates"
                        class="w-full lg:w-1/4"
                    />
                    <NumberField
                        v-model="asset.maximum_yearly_rates"
                        name="asset_maximum_yearly_rates"
                        label="asset_maximum_yearly_rates"
                        help="help_asset_maximum_yearly_rates"
                        class="w-full lg:w-1/4"
                    />
                </div>
            </template>
        </AccordionBasic>

        <!-- manual funding totals -->
        <AccordionBasic
            v-if="userStore.can('manage_manual_funding_totals')"
            title="manual_investments"
            help="help_manual_investments"
            class="mb-2"
        >
            <ToggleSwitch
                v-model="asset.manual_funding_totals"
                name="asset_manual_funding_totals"
                label="asset_manual_funding_totals"
                help="help_asset_manual_funding_totals"
            />
            <div class="lg:flex lg:gap-4">
                <NumberField
                    v-model="asset.base_funding_count"
                    name="asset_base_funding_count"
                    label="asset_base_funding_count"
                    help="help_asset_base_funding_count"
                    class="w-full lg:w-1/2"
                />
                <NumberField
                    v-model="asset.base_funding_total"
                    name="asset_base_funding_total"
                    label="asset_base_funding_total"
                    help="help_asset_base_funding_total"
                    class="w-full lg:w-1/2"
                />
            </div>
        </AccordionBasic>

        <!-- clearing systems -->
        <AccordionBasic
            title="clearing_systems"
            class="mb-2"
        >
            <SelectField
                v-model="asset.clearing_system"
                name="clearing_system"
                label="clearing_system"
                help="help_clearing_system"
                :options="clearingSystemOptions"
                :required="true"
            />
        </AccordionBasic>

        <!-- company details -->
        <AccordionBasic
            title="company_details"
            class="mb-2"
        >
            <TextField
                v-if="asset.deactivate_payment_account"
                v-model="asset.bank_account_holder"
                name="company_bank_account_holder"
                label="bank_account_holder"
                help="help_company_bank_account_holder"
            />

            <TextField
                v-if="asset.deactivate_payment_account"
                v-model="asset.deposit_account_holder_address"
                name="deposit_account_holder_address"
                label="deposit_account_holder_address"
                help="help_deposit_account_holder_address"
            />

            <TextField
                v-if="asset.deactivate_payment_account"
                v-model="asset.deposit_account_holder_country"
                label="deposit_account_holder_country"
                name="deposit_account_holder_country"
                help="help_deposit_account_holder_country"
            />

            <TextField
                v-model="asset.iban"
                :mask="ibanMask"
                name="company_iban"
                label="iban"
                help="help_company_iban"
            />

            <TextField
                v-model="asset.bic"
                name="company_bic"
                label="bic"
                help="help_company_bic"
            />
        </AccordionBasic>

        <!-- contract details -->
        <AccordionBasic
            title="contract_details"
            class="mb-2"
        >
            <TextField
                v-model="asset.contract_range"
                name="contract_range"
                label="contract_range"
                help="help_contract_range"
            />

            <NumberField
                v-model="asset.interest_rate"
                name="interest_rate"
                label="interest_rate"
                help="help_asset_interest_rate"
            />

            <TextField
                v-model="asset.target_yield"
                name="target_yield"
                label="target_yield"
                help="help_target_yield"
            />
        </AccordionBasic>

        <!-- signatures -->
        <AccordionBasic
            v-if="asset.signature_feature_active"
            title="signatures"
            class="mb-2"
        >
            <h3 class="mb-1 block text-sm font-medium">
                {{ $t('signature_types') }}
            </h3>
            <ToggleSwitch
                v-model="asset.signature_manual"
                :disabled="asset.signature_signteq"
                name="signature_type_manual"
                label="signature_type_manual"
            />
            <ToggleSwitch
                v-if="asset.signature_manual"
                v-model="asset.signature_manual_confirmation_required"
                name="signature_manual_confirmation_required"
                label="signature_manual_confirmation_required"
                help="help_signature_manual_confirmation_required"
            />
            <ToggleSwitch
                v-model="asset.signature_sproof"
                :disabled="asset.signature_signteq"
                label="signature_type_sproof"
                name="signature_type_sproof"
            />
            <ToggleSwitch
                v-if="asset.kyc_provider === 'signteq'"
                v-model="asset.signature_signteq"
                help="help_asset_signature_types"
                label="signature_type_signteq"
                name="signature_type_signteq"
            />
            <div v-if="asset.signature_signteq">
                <TextField
                    v-if="asset.signature_signteq"
                    v-model="asset.signature_signteq_subject"
                    name="asset_signature_signteq_subject"
                    label="asset_signature_signteq_subject"
                    help="help_asset_signature_signteq_subject"
                />
            </div>
            <div v-if="asset.signature_sproof">
                <SelectField
                    v-model="digitalSignatureType"
                    name="digital_signature_type"
                    label="digital_signature_types"
                    help="help_digital_signature_types"
                    :options="digitalSignatureTypeOptions"
                />
            </div>
            <!-- Commented out for now, as it's not used yet because of signteq's sign all at once issues  -->
            <!-- <div v-if="asset.signature_signteq">
                <h3 class="mb-1 mt-4 block text-sm font-medium">SIGNTEQ</h3>
                <ToggleSwitch
                    v-model="asset.sign_all_documents_at_once"
                    name="sign_all_documents"
                    label="sign_all_documents"
                    help="help_sign_all_documents"
                />
            </div> -->
        </AccordionBasic>
        <AccordionBasic
            title="payment_methods"
            class="mb-2"
        >
            <div class="flex items-center justify-start space-x-4">
                <ToggleSwitch
                    v-model="asset.payment_methods.credit_card"
                    name="credit_card"
                    label="credit_card"
                />
                <p
                    v-if="asset.payment_methods.credit_card"
                    class="text-sm text-gray-500"
                >
                    {{ $t('limit') }}:
                    {{ asset.payment_account?.limit_credit_card_order }}
                    {{ asset.payment_account?.currency }}
                </p>
            </div>
            <div class="flex items-center justify-start space-x-4">
                <ToggleSwitch
                    v-model="asset.payment_methods.wire_transfer"
                    name="wire_transfer"
                    label="wire_transfer"
                />
                <p
                    v-if="asset.payment_methods.wire_transfer"
                    class="text-sm text-gray-500"
                >
                    {{ $t('limit') }}:
                    {{ asset.payment_account?.limit_bank_transfer_order }}
                    {{ asset.payment_account?.currency }}
                </p>
            </div>
        </AccordionBasic>
        <AccordionBasic
            title="sm_marketplace"
            class="mb-2"
        >
            <div class="flex items-center justify-start space-x-4">
                <ToggleSwitch
                    v-model="asset.sm_marketplace"
                    name="sm_marketplace"
                    label="sm_marketplace"
                />
            </div>

            <div class="flex items-center justify-start space-x-4">
                <ToggleSwitch
                    v-model="asset.sm_trading_enabled"
                    name="sm_trading_enabled"
                    label="sm_trading_enabled"
                    help="help_sm_trading_enabled_asset"
                />
            </div>

            <hr class="border-gray-200" />

            <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
                <NumberField
                    v-model="asset.sm_target_price"
                    name="sm_target_price"
                    label="Richtpreis je Einheit (€)"
                    help="Legt den offiziellen Richtpreis für die Soft-Warning fest"
                />

                <NumberField
                    v-model="asset.sm_tolerance_band"
                    name="sm_tolerance_band"
                    label="sm_tolerance_band"
                    help="help_sm_tolerance_band"
                />

                <NumberField
                    v-model="asset.sm_subscription_ratio"
                    name="sm_subscription_ratio"
                    label="sm_subscription_ratio"
                    help="help_sm_subscription_ratio"
                    :step="0.00001"
                />
            </div>
            <hr class="border-gray-200" />
            <div class="grid grid-cols-1 gap-6 md:grid-cols-3">
                <NumberField
                    v-model="asset.sm_signing_deadline"
                    name="sm_signing_deadline"
                    label="sm_signing_deadline"
                    help="help_sm_signing_deadline"
                />
                <NumberField
                    v-model="asset.sm_payment_fulfillment_deadline"
                    name="sm_payment_fulfillment_deadline"
                    label="sm_payment_fulfillment_deadline"
                    help="help_sm_payment_fulfillment_deadline"
                />
                <NumberField
                    v-model="asset.sm_payment_confirmation_deadline"
                    name="sm_payment_confirmation_deadline"
                    label="sm_payment_confirmation_deadline"
                    help="help_sm_payment_confirmation_deadline"
                />
            </div>
            <div v-if="asset.signature_sproof">
                <SelectField
                    v-model="asset.sm_sproof_signature_type"
                    name="digital_signature_type"
                    label="digital_signature_types"
                    help="help_digital_signature_types"
                    :options="digitalSignatureTypeOptions"
                />
            </div>
            <hr class="border-gray-200" />
            <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
                <SelectField
                    v-model="asset.sm_exercise_campaign"
                    :options="campaigns"
                    name="sm_exercise_campaign"
                    label="sm_exercise_campaign"
                    help="help_sm_exercise_campaign"
                    :required="false"
                />

                <NumberField
                    v-model="asset.sm_transaction_fee"
                    name="sm_transaction_fee"
                    label="sm_transaction_fee"
                    help="help_sm_transaction_fee"
                />

                <div
                    class="card bg-surface border-divider rounded-md border p-4 md:col-span-2"
                >
                    <label class="text-body mb-3 block text-sm font-semibold">
                        Contract Template
                    </label>

                    <div
                        v-if="!hasTemplate"
                        class="border-divider bg-background-muted flex items-center justify-between rounded-md border border-dashed p-3"
                    >
                        <span class="text-muted text-sm"
                            >No contract template uploaded yet.</span
                        >
                        <CreateButton
                            v-if="userStore.can('update_asset')"
                            title="upload"
                            @click.stop="handleUpdateOrCreateTemplate"
                        />
                    </div>

                    <div
                        v-else
                        class="border-divider bg-background flex items-center justify-between rounded-md border p-3"
                    >
                        <div class="flex items-center space-x-3">
                            <i
                                class="fas fa-file-pdf text-danger-500 text-lg"
                            ></i>
                            <span
                                class="text-body font-mono text-sm font-medium"
                            >
                                {{ asset.sm_template?.name }}
                            </span>
                        </div>

                        <div class="flex items-center space-x-2">
                            <button
                                type="button"
                                class="btn btn-secondary btn-sm"
                                @click.stop="handleViewContractTemplate"
                            >
                                <i class="fas fa-eye mr-1"></i> View
                            </button>
                            <button
                                v-if="userStore.can('update_asset')"
                                type="button"
                                class="btn btn-danger-outline btn-sm"
                                @click.stop="handleDeleteTemplateConfirmation"
                            >
                                <i class="fas fa-trash-alt mr-1"></i> Delete
                            </button>
                        </div>
                    </div>

                    <div
                        v-if="asset.sm_template"
                        class="md:col-span-2"
                    >
                        <TextField
                            v-model="
                                asset.sm_template.custom_properties
                                    .sm_buyer_signature_position
                            "
                            :rows="5"
                            name="sm_buyer_signature_position"
                            label="sm_buyer_signature_position"
                        />
                        <TextField
                            v-model="
                                asset.sm_template.custom_properties
                                    .sm_seller_signature_position
                            "
                            :rows="5"
                            name="sm_seller_signature_position"
                            label="sm_seller_signature_position"
                        />
                    </div>
                </div>

                <div class="md:col-span-2">
                    <TextField
                        v-model="stringEscalationEmails"
                        name="sm_escalation_emails"
                        label="Asset-spezifische Eskalations-E-Mails"
                        help="Mehrere E-Mails mit Komma trennen (z.B. support@oekostrom.at, admin@oekostrom.at)"
                    />
                </div>

                <AssetRiskManager
                    v-model="asset.sm_asset_risks"
                    class="md:col-span-2"
                />
            </div>
        </AccordionBasic>
        <AssetClassesAccordion
            v-if="asset.asset_class"
            :asset="asset"
        />
        <AccordionBasic
            title="return_calculator"
            class="mb-2"
        >
            <div class="flex items-center justify-start space-x-4">
                <ToggleSwitch
                    v-model="asset.return_calculator_active"
                    name="return_calculator_active"
                    label="return_calculator_active"
                />
            </div>
        </AccordionBasic>
        <!--
        Right now we are commenting this as it does not make much sense to have it
        for the SecuPay in the asset level as there can be only one payment account
        per platform. But the correct relationship should be one platform can have
        multiple payment accounts. We can enable this after we implement correct
        relations and MangoPay.
        -->
        <!-- <AccordionBasic title="payment_account">
            <div class="mt-4">
                <SelectField
                    v-model="asset.payment_account_id"
                    name="payment_account_id"
                    :placeholder="$t('payment_account')"
                    label="payment_account"
                    help="help_payment_account"
                    :options="
                        paymentAccountOptions.map((paymentAccountOption) => ({
                            value: paymentAccountOption.id,
                            label: paymentAccountOption.name,
                        }))
                    "
                    title="payment_account_id"
                    class="mb-6"
                />
            </div>
        </AccordionBasic> -->
        <ModalWrapper v-if="isModalOpen" />
    </PageWrapper>
</template>
<script setup lang="ts">
import { useMask, useModal } from '@composables/common'
import {
    AccordionBasic,
    NumberField,
    SelectField,
    TextField,
    ToggleSwitch,
} from '@src/components'
import { useEnum } from '@src/composables/common'
import { useContractTemplateManager } from '@composables/secondaryMarket/useContractTemplateManager'
import useEditLock from '@src/composables/editLock'
import {
    AssetCategory,
    AssetClass,
    AssetStatus,
    AssetSubStatus,
    StockType,
} from '@src/enums'
import BackButton from '@src/partials/actions/BackButton.vue'
import SaveButton from '@src/partials/actions/SaveButton.vue'
import PageWrapper from '@src/partials/pages/PageWrapper.vue'
import {
    Asset,
    ContractTemplate,
    Issuer,
    PaymentMethods,
    Platform,
} from '@src/types'
import { PaymentAccount } from '@src/types/PaymentAccount'
import { getAsset, getAssetSubStatus, updateAsset } from '@src/utils/api/assets'
import { normalizeAssetClassToggles } from '@src/utils/helpers/normalizeAssetClassToggles'
import { getCurrencies } from '@src/utils/api/currencies'
import { getPaymentAccounts } from '@src/utils/api/paymentAccounts'
import { getPlatforms } from '@src/utils/api/platforms'
import { useCountryStore } from '@src/utils/stores/countries'
import { useToastStore } from '@src/utils/stores/toast'
import { useUserStore } from '@src/utils/stores/user'
import { getIssuers } from '@utils/api/issuers'
import { evaluate } from 'mathjs'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import CreateButton from '@partials/actions/CreateButton.vue'
import { getCampaigns } from '@utils/api/campaigns'
import AssetRiskManager from '@partials/secondaryMarket/AssetRiskManager.vue'
import AssetClassesAccordion from '@src/partials/assets/AssetClassesAccordion.vue'

const router = useRouter()
const route = useRoute()
const toast = useToastStore()
const i18n = useI18n()
const { mapEnumToArray, reduceEnumValue } = useEnum()
const { ModalWrapper, openModal } = useModal()
const { ibanMask } = useMask()
const currencyOptions = ref<string[]>([])
const countryStore = useCountryStore()
const countries = ref<{ label: string; value: string | null }[]>([])
const userStore = useUserStore()
const isModalOpen = ref(false)

useEditLock('asset', route.params.id as string)

const asset = ref<Asset>(new Asset())
const contractTemplate = ref<ContractTemplate | null>(null)

// Legacy STOCK_EMISSION fields are gated on `asset.category`, the new
// asset_class-driven fields below widen that gate with OR (never AND) so
// existing STOCK_EMISSION assets that haven't been backfilled with an
// asset_class value yet keep showing these fields.
const isStockEmissionLegacy = computed(
    () => asset.value.category === AssetCategory.STOCK_EMISSION
)

function assetClassIn(classes: AssetClass[]): boolean {
    return (
        !!asset.value.asset_class && classes.includes(asset.value.asset_class)
    )
}

// voting_rights_per_share, number_of_stocks, minimum_investment,
// stock_increments, maximum_total_investment_value
const showVotingRightsFieldGroup = computed(
    () =>
        isStockEmissionLegacy.value ||
        assetClassIn([
            AssetClass.SHARE,
            AssetClass.PARTICIPATION_CERT,
            AssetClass.COOPERATIVE_SHARE,
        ])
)
// nominal_value
const showNominalValue = computed(
    () =>
        isStockEmissionLegacy.value ||
        assetClassIn(
            Object.values(AssetClass).filter(
                (assetClass) => assetClass !== AssetClass.COOPERATIVE_SHARE
            )
        )
)
// agio
const showAgio = computed(
    () =>
        isStockEmissionLegacy.value ||
        assetClassIn([
            AssetClass.SHARE,
            AssetClass.PARTICIPATION_CERT,
            AssetClass.BOND,
            AssetClass.CONVERTIBLE_BOND,
        ])
)
// price_per_stock, stock_type
const showPricePerStockFieldGroup = computed(
    () =>
        isStockEmissionLegacy.value ||
        assetClassIn([AssetClass.SHARE, AssetClass.PARTICIPATION_CERT])
)

// for stock emission calculations
const minimum_investment_shares = ref<number>(0)
const sharesDebounceTimeout = ref<ReturnType<typeof setTimeout> | null>(null)
const amountDebounceTimeout = ref<ReturnType<typeof setTimeout> | null>(null)
const stockIncrementsDebounceTimeout = ref<ReturnType<
    typeof setTimeout
> | null>(null)
const numberOfStocksDebounceTimeout = ref<ReturnType<typeof setTimeout> | null>(
    null
)

const platformOptions = ref<{ label: string; value: string }[]>([])
const issuerOptions = ref<{ label: string; value: string }[]>([])
const assetSubStatuses = ref<AssetSubStatus[]>([])
const stringEscalationEmails = ref('')
const stringRequiredBaseDataFields = ref('')
const stringQuickAmounts = ref('')
const campaigns = ref<{ label: string; value: string }[]>([])

const digitalSignatureType = computed<'qualified' | 'advanced' | null>({
    get: () =>
        asset.value.require_qualified_signature
            ? 'qualified'
            : asset.value.require_advanced_qualified_signature
            ? 'advanced'
            : null,
    set: (value) => {
        asset.value.require_qualified_signature = value === 'qualified'
        asset.value.require_advanced_qualified_signature = value === 'advanced'
    },
})

const digitalSignatureTypeOptions = [
    { label: i18n.t('require_qualified_signature'), value: 'qualified' },
    {
        label: i18n.t('require_advanced_qualified_signature'),
        value: 'advanced',
    },
]

const clearingSystemOptions = ref<{ label: string; value: string }[]>([
    {
        label: i18n.t('clearing_system_ci'),
        value: 'CI',
    },
    {
        label: i18n.t('clearing_system_emission'),
        value: 'EMISSION',
    },
    {
        label: i18n.t('clearing_system_stock_emission'),
        value: 'STOCK_EMISSION',
    },
])

function goToDetails(): void {
    router.push({ name: 'asset', params: { id: asset.value.asset_uid } })
}

function toAssets(): void {
    router.push({ name: 'assets' })
}

const {
    hasTemplate,
    handleDeleteTemplateConfirmation,
    handleUpdateOrCreateTemplate,
} = useContractTemplateManager(asset, contractTemplate, openModal, isModalOpen)

function handleViewContractTemplate() {
    const url = contractTemplate.value?.url ?? asset.value.sm_template?.url
    if (url) {
        window.open(url)
    }
}

async function saveAsset() {
    // Create a clean deep copy of the asset object to prevent mutating UI state directly
    const assetToSave = JSON.parse(JSON.stringify(asset.value))

    // Parse escalation emails string into an array
    assetToSave.sm_escalation_emails = stringEscalationEmails.value
        ? stringEscalationEmails.value
              .split(',')
              .map((e) => e.trim())
              .filter(Boolean)
        : []

    // Parse required base data fields string into an array
    assetToSave.sm_required_base_data_fields =
        stringRequiredBaseDataFields.value
            ? stringRequiredBaseDataFields.value
                  .split(',')
                  .map((f) => f.trim())
                  .filter(Boolean)
            : []

    // Parse quick amounts string (one number per line) into an array
    assetToSave.quick_amounts = stringQuickAmounts.value
        ? stringQuickAmounts.value
              .split('\n')
              .map((v) => v.trim())
              .filter(Boolean)
              .map(Number)
        : []

    // Prevent re-processing stale temp files on consecutive saves.
    // Keep template metadata (like 'name') to satisfy backend validation when sm_marketplace is enabled,
    // but remove 'path' if the template has already been persisted (has an ID).
    if (assetToSave.sm_template) {
        if (assetToSave.sm_template.id || contractTemplate.value?.id) {
            delete assetToSave.sm_template.path
        }
    }

    const updatedAssetResponse = await updateAsset(assetToSave)

    // Sync local reactive state with fresh backend response data
    if (updatedAssetResponse?.data?.asset) {
        asset.value = updatedAssetResponse.data.asset
        contractTemplate.value = updatedAssetResponse.data.asset.sm_template
    }

    toast.success(
        i18n.t('toast_success_asset_saved', { asset: asset.value.asset_title })
    )
}

async function loadPlatformNameOptions(search: string) {
    const initialPlatformsRequest = await getPlatforms({
        'filter[platform_name]': search,
    })
    platformOptions.value = initialPlatformsRequest.data.platforms.map(
        (platform: Platform) => {
            return {
                label: platform.platform_name as string,
                value: platform.platform_name as string,
            }
        }
    )
}

async function loadIssuerOptions(search: string) {
    const request = await getIssuers({
        'filter[name]': search,
        'filter[platform_name]': asset.value.platform_name,
    })
    issuerOptions.value = request.data.issuers.map((issuer: Issuer) => {
        return {
            value: issuer.id as string,
            label: issuer.name + ' (' + issuer.platform_name + ')',
        }
    })
}

const paymentAccountOptions = ref<PaymentAccount[]>([])

async function loadPaymentAccountOptions() {
    const paymentAccountsRequest = await getPaymentAccounts()
    paymentAccountOptions.value = paymentAccountsRequest.data.payment_accounts
}

onMounted(async () => {
    if (route.params.id) {
        const assetRequest = await getAsset(route.params.id as string)
        asset.value = Object.assign(new Asset(), assetRequest.data.asset)
        normalizeAssetClassToggles(asset.value)
        if (asset.value.payment_methods === null) {
            asset.value.payment_methods = new PaymentMethods()
        }
        if (asset.value.payment_account?.limit_credit_card_order) {
            asset.value.payment_account.limit_credit_card_order =
                asset.value.payment_account.limit_credit_card_order?.amount ?? 0
        }
        if (asset.value.payment_account?.limit_bank_transfer_order) {
            asset.value.payment_account.limit_bank_transfer_order =
                asset.value.payment_account.limit_bank_transfer_order?.amount ??
                0
        }

        if (
            asset.value.sm_template &&
            !asset.value.sm_template.custom_properties
        ) {
            asset.value.sm_template.custom_properties = {}
        }

        if (asset.value.platform_name !== null) {
            platformOptions.value = [
                {
                    value: asset.value.platform_name as string,
                    label: asset.value.platform_name as string,
                },
            ]
        }
        if (
            asset.value.issuer?.id !== null &&
            asset.value.issuer?.name !== null
        ) {
            issuerOptions.value = [
                {
                    value: asset.value.issuer?.id as string,
                    label:
                        asset.value.issuer?.name +
                        ' (' +
                        asset.value.issuer?.platform_name +
                        ')',
                },
            ]
        }

        contractTemplate.value = asset.value.sm_template
    }

    loadData()
    fetchCampaigns()

    const currenciesRequest = await getCurrencies()
    await retrieveAssetSubStatus()
    currencyOptions.value = currenciesRequest.data.currencies

    // populate minimum_investment_shares
    if (
        asset.value.minimum_investment &&
        asset.value.minimum_investment !== 0 &&
        asset.value.price_per_stock &&
        asset.value.price_per_stock !== 0
    ) {
        minimum_investment_shares.value =
            asset.value.minimum_investment / asset.value.price_per_stock
    } else {
        minimum_investment_shares.value = 1
    }

    await loadPaymentAccountOptions()
})

const retrieveAssetSubStatus = async () => {
    if (asset.value.status) {
        assetSubStatuses.value = []
        const assetSubStatusRequest = await getAssetSubStatus(
            asset.value.status
        )
        if (assetSubStatusRequest.data.success) {
            assetSubStatuses.value = assetSubStatusRequest.data.sub_status ?? {}
            if (
                assetSubStatuses.value === null ||
                Object.values(assetSubStatuses.value).indexOf(
                    asset.value.sub_status
                ) === -1
            ) {
                asset.value.sub_status = null
            }
        }
    }
}

const fetchCampaigns = async () => {
    if (!asset.value?.asset_uid) return

    try {
        const response = await getCampaigns({
            'filter[asset_uid]': asset.value.asset_uid,
        })

        const variations = response.data.asset_variations || []

        campaigns.value = variations.map((campaign) => ({
            label: campaign.asset_title,
            value: campaign.asset_var_uid,
        }))
    } catch (error) {
        console.error('Error fetching campaigns:', error)
    }
}

const loadData = async () => {
    countries.value = await countryStore.loadAndSortCountriesOptionsAsync()
}

watch(i18n.locale, async () => {
    loadData()
})

watch(
    () => asset.value,
    (newAsset) => {
        if (newAsset) {
            stringEscalationEmails.value = newAsset.sm_escalation_emails
                ? newAsset.sm_escalation_emails.join(', ')
                : ''

            stringRequiredBaseDataFields.value =
                newAsset.sm_required_base_data_fields
                    ? newAsset.sm_required_base_data_fields.join(', ')
                    : ''

            stringQuickAmounts.value = newAsset.quick_amounts
                ? newAsset.quick_amounts.join('\n')
                : ''
        }
    },
    { immediate: true }
)

// Keep bank_account_holder null when deactivate_payment_account is false
watch(
    () => asset.value.deactivate_payment_account,
    (isDeactivated) => {
        if (isDeactivated === false) {
            asset.value.bank_account_holder = null
        }
    }
)

// Keep the legacy `category` field in sync going forward for shares (the new
// asset_class-driven fields still need the OR-widening above regardless,
// since participation_cert/cooperative_share have no equivalent legacy
// AssetCategory value to sync to). Also default `transferable` to false the
// moment COOPERATIVE_SHARE is newly selected, but only if the user hasn't
// already set an explicit value themselves - an explicit later change must
// win, so this must not re-run on every re-render/watch trigger.
watch(
    () => asset.value.asset_class,
    (newVal) => {
        if (newVal === AssetClass.SHARE) {
            asset.value.category = AssetCategory.STOCK_EMISSION
        }
        if (
            newVal === AssetClass.COOPERATIVE_SHARE &&
            (asset.value.transferable === null ||
                asset.value.transferable === undefined)
        ) {
            asset.value.transferable = false
        }
        // The accordion is unmounted without a class, so its own watch can't do this.
        normalizeAssetClassToggles(asset.value)
    }
)

// Null out fields once they become hidden by the funding-limits vs. stock-value
// gate above (driven by `category`/`asset_class` together) - otherwise a stale
// value left over from before the switch fails the backend's
// required_if/nullable-when-stock-emission rules and blocks saving. Runs
// immediately too, so loading an asset whose data already matches its class
// doesn't need a manual re-trigger to self-heal.
watch(
    [() => asset.value.category, () => asset.value.asset_class],
    () => {
        if (asset.value.category === AssetCategory.STOCK_EMISSION) {
            asset.value.funding_min = null
            asset.value.funding_max = null
            asset.value.investment_min = null
            asset.value.investment_max = null
            asset.value.step_level = null
            stringQuickAmounts.value = ''
        }
        if (!showVotingRightsFieldGroup.value) {
            asset.value.voting_rights_per_share = null
            asset.value.number_of_stocks = null
            asset.value.minimum_investment = null
            asset.value.stock_increments = null
            asset.value.maximum_total_investment_value = null
        }
        if (!showNominalValue.value) {
            asset.value.nominal_value = null
        }
        if (!showAgio.value) {
            asset.value.agio = null
        }
        if (!showPricePerStockFieldGroup.value) {
            asset.value.price_per_stock = null
            asset.value.stock_type = null
        }
        if (
            !showPricePerStockFieldGroup.value &&
            !showVotingRightsFieldGroup.value
        ) {
            asset.value.isin = null
        }
    },
    { immediate: true }
)

watch(
    () => asset.value.investment_max,
    (newVal) => {
        if (newVal) {
            const credit_card_limit =
                asset.value.payment_account?.limit_credit_card_order ?? 0
            const wire_transfer_limit =
                asset.value.payment_account?.limit_bank_transfer_order ?? 0
            const biggest_limit = Math.max(
                credit_card_limit,
                wire_transfer_limit
            )
            if (biggest_limit < newVal) {
                toast.error(
                    i18n.t(
                        'toast_investment_max_is_greater_than_the_biggest_limit'
                    )
                )
            }
        }
    }
)

watch(
    () => minimum_investment_shares.value,
    () => {
        if (sharesDebounceTimeout.value) {
            clearTimeout(sharesDebounceTimeout.value)
        }

        sharesDebounceTimeout.value = setTimeout(() => {
            if (
                minimum_investment_shares.value &&
                asset.value.price_per_stock
            ) {
                asset.value.minimum_investment = evaluate(
                    `${minimum_investment_shares.value} * ${asset.value.price_per_stock}`
                )
            }
        }, 1000)
    }
)

watch(
    () => asset.value.minimum_investment,
    () => {
        if (amountDebounceTimeout.value) {
            clearTimeout(amountDebounceTimeout.value)
        }

        amountDebounceTimeout.value = setTimeout(() => {
            if (
                asset.value?.minimum_investment &&
                asset.value?.price_per_stock &&
                evaluate(
                    `${asset.value?.minimum_investment} % ${asset.value?.price_per_stock}`
                ) !== 0
            ) {
                asset.value.minimum_investment -= evaluate(
                    `${asset.value.minimum_investment} % ${asset.value.price_per_stock}`
                )
            }
            if (asset.value.minimum_investment && asset.value.price_per_stock) {
                minimum_investment_shares.value = evaluate(
                    `${asset.value.minimum_investment} / ${asset.value.price_per_stock}`
                )
            }
        }, 1000)
    }
)

watch(
    () => asset.value.price_per_stock,
    (newVal) => {
        if (newVal && asset.value.number_of_stocks) {
            asset.value.maximum_total_investment_value =
                newVal * asset.value.number_of_stocks
        }
        if (minimum_investment_shares.value && asset.value.price_per_stock) {
            asset.value.minimum_investment = evaluate(
                `${minimum_investment_shares.value} * ${asset.value.price_per_stock}`
            )
        }
    }
)

watch(
    () => asset.value.stock_increments,
    (newVal) => {
        if (stockIncrementsDebounceTimeout.value) {
            clearTimeout(stockIncrementsDebounceTimeout.value)
        }

        stockIncrementsDebounceTimeout.value = setTimeout(() => {
            if (
                newVal &&
                asset.value.number_of_stocks &&
                evaluate(`${asset.value.number_of_stocks} % ${newVal}`) !== 0
            ) {
                if (
                    evaluate(`${asset.value.number_of_stocks} % ${newVal}`) >
                    newVal / 2
                ) {
                    asset.value.number_of_stocks +=
                        newVal -
                        evaluate(`${asset.value.number_of_stocks} % ${newVal}`)
                } else {
                    asset.value.number_of_stocks -= evaluate(
                        `${asset.value.number_of_stocks} % ${newVal}`
                    )
                }
            }
        }, 1000)
    }
)

watch(
    () => asset.value.number_of_stocks,
    (newVal) => {
        if (numberOfStocksDebounceTimeout.value) {
            clearTimeout(numberOfStocksDebounceTimeout.value)
        }

        numberOfStocksDebounceTimeout.value = setTimeout(() => {
            if (newVal && asset.value.price_per_stock) {
                asset.value.maximum_total_investment_value = evaluate(
                    `${newVal} * ${asset.value.price_per_stock}`
                )
            }

            if (
                asset.value.number_of_stocks &&
                asset.value.stock_increments &&
                evaluate(
                    `${asset.value.number_of_stocks} % ${asset.value.stock_increments}`
                ) !== 0
            ) {
                if (
                    evaluate(
                        `${asset.value.number_of_stocks} % ${asset.value.stock_increments}`
                    ) >
                    asset.value.stock_increments / 2
                ) {
                    asset.value.number_of_stocks +=
                        asset.value.stock_increments -
                        evaluate(
                            `${asset.value.number_of_stocks} % ${asset.value.stock_increments}`
                        )
                } else {
                    asset.value.number_of_stocks -= evaluate(
                        `${asset.value.number_of_stocks} % ${asset.value.stock_increments}`
                    )
                }
            }
        }, 1000)
    }
)

watch(
    () => asset.value.agio,
    (newVal) => {
        if (
            typeof newVal === 'number' &&
            typeof asset.value.nominal_value === 'number'
        ) {
            asset.value.price_per_stock =
                asset.value.nominal_value + (asset.value?.agio ?? 0)
        }
    }
)

watch(
    () => asset.value.nominal_value,
    (newVal) => {
        if (
            typeof newVal === 'number' &&
            typeof asset.value.agio === 'number'
        ) {
            asset.value.price_per_stock = newVal + (asset.value?.agio ?? 0)
        }
    }
)

watch(
    () => asset.value.payment_methods,
    (newVal) => {
        const paymentAccount = asset.value.payment_account
        const availablePaymentMethods = Object.keys(newVal).filter(
            (key) => newVal[key as keyof PaymentMethods] === true
        )
        const limit = availablePaymentMethods.reduce((max, key) => {
            let methodLimit = 0
            switch (key) {
                case 'credit_card':
                    methodLimit = paymentAccount?.limit_credit_card_order ?? 0
                    break
                case 'wire_transfer':
                    methodLimit = paymentAccount?.limit_bank_transfer_order ?? 0
                    break
                default:
                    methodLimit = 0
            }
            return Math.max(max, methodLimit)
        }, 0)
        const investment_max = asset.value?.investment_max ?? 0
        if (limit < investment_max) {
            toast.error(
                i18n.t('toast_investment_max_is_greater_than_the_biggest_limit')
            )
        }
        if (
            Object.keys(newVal).every(
                (key) => newVal[key as keyof PaymentMethods] === false
            )
        ) {
            toast.error(i18n.t('toast_error_payment_methods_required'))
            nextTick(() => {
                asset.value.payment_methods.credit_card = true
            })
        }
    },
    { deep: true, immediate: false }
)
</script>
<style lang="scss">
div.asset-status {
    button.vs__clear {
        display: none; // @TODO: Vue3 SelectField Component :clearable prop not working!
    }
}
</style>
